import React, { useState, useEffect } from 'react';

import { useMediaQuery } from 'react-responsive';
import 'react-circular-progressbar/dist/styles.css';
import 'react-phone-input-2/lib/style.css';

import Text from 'common/components/Text/Text';
import { CommonHash } from 'app/types';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import EnglishForm from './EnglishForm';
import {
  SessionState,
  deleteEnglishExamFormSession,
  deleteTechnicalExamFormSession,
} from 'features/session/sessionSlice';
import { useDeleteHistoryMutation, useDeleteBackofficeHistoryMutation } from 'services/exploreApi';

import TechnicalForm from './TechnicalForm';
import styles from './UserInformation.module.scss';

interface CompProps {
  answers: CommonHash;
  setAnswer: (h: CommonHash) => void;
  setSaveForm: (b: boolean) => void;
}

const TestsForm = ({ answers, setAnswer, setSaveForm }: CompProps): React.ReactElement => {
  const { user } = useAppSelector(({ session }: { session: SessionState }) => session);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [englishExams, setEnglishExams] = useState([
    <EnglishForm index={0} key={0} answers={answers} setAnswer={setAnswer} />,
  ]);

  const [technicalExams, setTechnicalExams] = useState([
    <TechnicalForm index={0} key={0} answers={answers} setAnswer={setAnswer} />,
  ]);

  let deleteHistory: any;
  const dispatch = useAppDispatch();
  if (location.pathname.includes('backoffice')) {
    [deleteHistory] = useDeleteBackofficeHistoryMutation();
  } else {
    [deleteHistory] = useDeleteHistoryMutation();
  }

  const handleAddEnglishTest = (e: any) => {
    setEnglishExams([
      ...englishExams,
      <EnglishForm
        index={englishExams.length}
        key={englishExams.length}
        answers={answers}
        setAnswer={setAnswer}
      />,
    ]);
  };

  const handleAddTechnicalTest = (e: any) => {
    setTechnicalExams([
      ...technicalExams,
      <TechnicalForm
        index={technicalExams.length}
        key={technicalExams.length}
        answers={answers}
        setAnswer={setAnswer}
      />,
    ]);
  };

  const handleRemoveEnglishTest = (index: number) => {
    const list = [...englishExams];
    list.splice(index, 1);
    setEnglishExams(list);
    const englishExamsList = [...answers['english_exams']];
    englishExamsList.splice(index, 1);
    setAnswer((a: any) => ({
      ...a,
      english_exams: englishExamsList,
    }));
    deleteHistory({ index: index, type: 'english_exam', id: user?.id });
    dispatch(deleteEnglishExamFormSession(index));
  };

  const handleRemoveTechnicalTest = (index: number) => {
    const list = [...technicalExams];
    list.splice(index, 1);
    setTechnicalExams(list);
    const technicalExamsList = [...answers['technical_exams']];
    technicalExamsList.splice(index, 1);
    setAnswer((a: any) => ({
      ...a,
      technical_exams: technicalExamsList,
    }));
    deleteHistory({ index: index, type: 'technical_exam' });
    dispatch(deleteTechnicalExamFormSession(index));
  };

  useEffect(() => {
    if (user?.['english_exams'] && user['english_exams'].length) {
      const prevEnglishExams: any = [];
      user['english_exams'].map((h, index) => {
        prevEnglishExams.push(
          <EnglishForm index={index} key={index} answers={answers} setAnswer={setAnswer} />
        );
      });
      setEnglishExams(prevEnglishExams);
    }
    if (user?.['technical_exams'] && user['technical_exams'].length) {
      const prevTechnicalExams: any = [];
      user['technical_exams'].map((h, index) => {
        prevTechnicalExams.push(
          <TechnicalForm index={index} key={index} answers={answers} setAnswer={setAnswer} />
        );
      });
      setTechnicalExams(prevTechnicalExams);
    }
  }, []);

  return (
    <div className={styles.stepContainer}>
      <Text size={16} weight={600} color="primary" inlineStyle={{ marginBottom: '24px' }}>
        English tests
      </Text>
      {englishExams.map((englishExam, index) =>
        index == 0 ? (
          englishExam
        ) : (
          <div className={styles.historyContainer} key={index}>
            <hr />
            <div className={styles.deleteHistory} onClick={() => handleRemoveEnglishTest(index)}>
              <i className="bi bi-trash"></i>
            </div>
            {englishExam}
          </div>
        )
      )}
      <div className={styles.addMoreTestBox} onClick={handleAddEnglishTest}>
        Add more english exam +
      </div>
      <Text
        size={16}
        weight={600}
        color="primary"
        inlineStyle={{ marginBottom: '24px', marginTop: '16px' }}
      >
        Academic tests(optional)
      </Text>
      {technicalExams.map((technicalExam, index) =>
        index == 0 ? (
          technicalExam
        ) : (
          <div className={styles.historyContainer} key={index}>
            <hr />
            <div className={styles.deleteHistory} onClick={() => handleRemoveTechnicalTest(index)}>
              <i className="bi bi-trash"></i>
            </div>
            {technicalExam}
          </div>
        )
      )}
      <div className={styles.addMoreTestBox} onClick={handleAddTechnicalTest}>
        Add more academic exam +
      </div>

      {isMobile && (
        <button
          className={styles.nextStepButton}
          onClick={() => {
            setSaveForm(true);
            setIsLoading(true);
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className={`spinner-border ${styles.loading}`} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            'Finish'
          )}
        </button>
      )}
    </div>
  );
};

export default TestsForm;
